import { STTService, TranscriptionResult } from "../types/stt";

// Declare the webkitAudioContext type for Safari support
declare global {
  interface Window {
    webkitAudioContext: typeof AudioContext;
  }
}

export class DeepgramSTTService implements STTService {
  private apiKey: string;
  private model: string;

  constructor(apiKey: string, model: string = "nova-2") {
    this.apiKey = apiKey;
    this.model = model;
  }

  setModel(model: string) {
    this.model = model;
  }

  get name() {
    return `Deepgram Nova-${this.model === "nova-2" ? "2" : "3"}`;
  }

  private getModelForAPI() {
    // Use the model name as is
    return this.model;
  }

  private getAPIParams() {
    const baseParams = {
      model: this.getModelForAPI(),
      smart_format: "true",
      punctuate: "true",
    };

    // Add language detection for Nova-3, explicit language for Nova-2
    const params =
      this.model === "nova-3"
        ? { ...baseParams, detect_language: "true" }
        : { ...baseParams, language: "zh" };

    return params;
  }

  async transcribe(audioBlob: Blob): Promise<TranscriptionResult> {
    const startTime = performance.now();

    try {
      // For uploaded files, we need to ensure we're sending the correct mime type
      // and potentially convert the format if needed
      let processedBlob = audioBlob;
      let contentType = audioBlob.type;

      // If the blob doesn't have a type or is not a supported audio format,
      // we'll treat it as audio/wav
      if (!contentType || !contentType.startsWith("audio/")) {
        contentType = "audio/wav";
      }

      // For uploaded files that might be in different formats,
      // we'll use the Audio Context to ensure compatibility
      if (!audioBlob.type.includes("webm")) {
        try {
          const arrayBuffer = await audioBlob.arrayBuffer();
          const audioContext = new (window.AudioContext ||
            window.webkitAudioContext)();
          const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

          // Convert to WAV format
          const wavBuffer = await new Promise<Blob>((resolve) => {
            const numberOfChannels = audioBuffer.numberOfChannels;
            const length = audioBuffer.length * numberOfChannels * 2;
            const buffer = new ArrayBuffer(44 + length);
            const view = new DataView(buffer);

            // Write WAV header
            const writeString = (
              view: DataView,
              offset: number,
              string: string
            ) => {
              for (let i = 0; i < string.length; i++) {
                view.setUint8(offset + i, string.charCodeAt(i));
              }
            };

            writeString(view, 0, "RIFF");
            view.setUint32(4, 36 + length, true);
            writeString(view, 8, "WAVE");
            writeString(view, 12, "fmt ");
            view.setUint32(16, 16, true);
            view.setUint16(20, 1, true);
            view.setUint16(22, numberOfChannels, true);
            view.setUint32(24, audioBuffer.sampleRate, true);
            view.setUint32(
              28,
              audioBuffer.sampleRate * numberOfChannels * 2,
              true
            );
            view.setUint16(32, numberOfChannels * 2, true);
            view.setUint16(34, 16, true);
            writeString(view, 36, "data");
            view.setUint32(40, length, true);

            // Write audio data
            const offset = 44;
            for (let i = 0; i < audioBuffer.numberOfChannels; i++) {
              const channel = audioBuffer.getChannelData(i);
              for (let j = 0; j < channel.length; j++) {
                const sample = Math.max(-1, Math.min(1, channel[j]));
                const value = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
                view.setInt16(
                  offset + (j * numberOfChannels + i) * 2,
                  value,
                  true
                );
              }
            }

            resolve(new Blob([buffer], { type: "audio/wav" }));
          });

          processedBlob = wavBuffer;
          contentType = "audio/wav";
        } catch (err) {
          console.warn(
            "Audio conversion failed, trying with original format:",
            err
          );
        }
      }

      // Send the processed audio to Deepgram
      const params = new URLSearchParams(this.getAPIParams());
      const response = await fetch(
        `https://api.deepgram.com/v1/listen?${params.toString()}`,
        {
          method: "POST",
          headers: {
            Authorization: `Token ${this.apiKey}`,
            "Content-Type": contentType,
          },
          body: processedBlob,
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage =
            errorData.error || errorData.message || response.statusText;
        } catch {
          errorMessage = errorText || response.statusText;
        }
        throw new Error(`Deepgram API error: ${errorMessage}`);
      }

      const data = await response.json();
      const endTime = performance.now();
      const processingTime = (endTime - startTime) / 1000;

      // Clean up spaces between Chinese characters
      const text = data.results?.channels[0]?.alternatives[0]?.transcript || "";
      const cleanedText = text.replace(
        /(?<=[\u4e00-\u9fff])\s+(?=[\u4e00-\u9fff])/g,
        ""
      );

      return {
        model: this.name,
        text: cleanedText,
        confidence: data.results?.channels[0]?.alternatives[0]?.confidence || 0,
        processingTime,
      };
    } catch (error) {
      if (error instanceof Error) {
        throw error; // Re-throw the error with its original message
      }
      throw new Error("Unknown Deepgram error occurred");
    }
  }
}
